import axios from "axios";
import API_CONFIG from "./api-config.js";

// Create axios instance with proper configuration
const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  withCredentials: true,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Cache for API responses
const apiCache = new Map();

// Retry mechanism
const retryRequest = async (config, retries = API_CONFIG.MAX_RETRIES) => {
  try {
    return await axiosInstance(config);
  } catch (error) {
    if (retries > 0 && (error.code === 'ECONNABORTED' || error.response?.status >= 500)) {
      console.log(`🔄 Retrying request (${API_CONFIG.MAX_RETRIES - retries + 1}/${API_CONFIG.MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY));
      return retryRequest(config, retries - 1);
    }
    throw error;
  }
};

// Cache interceptor
const cacheInterceptor = {
  request: (config) => {
    // Skip cache for non-GET requests
    if (config.method !== 'get') {
      return config;
    }

    const cacheKey = `${config.method}:${config.url}:${JSON.stringify(config.params)}`;
    const cachedResponse = apiCache.get(cacheKey);

    if (cachedResponse && Date.now() - cachedResponse.timestamp < API_CONFIG.CACHE_DURATION) {
      console.log('📦 Returning cached response for:', config.url);
      return Promise.resolve({
        ...cachedResponse.data,
        config,
        request: config,
        status: 200,
        statusText: 'OK',
        headers: cachedResponse.headers,
        cached: true
      });
    }

    return config;
  },
  response: (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' && response.status === 200) {
      const cacheKey = `${response.config.method}:${response.config.url}:${JSON.stringify(response.config.params)}`;
      apiCache.set(cacheKey, {
        data: response,
        timestamp: Date.now(),
        headers: response.headers
      });
    }
    return response;
  }
};

// Add request interceptor for logging and caching
axiosInstance.interceptors.request.use(
  (config) => {
    if (import.meta.env.DEV) {
      console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
    }
    
    // Apply cache interceptor
    return cacheInterceptor.request(config);
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and caching
axiosInstance.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log('✅ API Response:', response.status, response.config.url);
    }
    
    // Apply cache interceptor
    return cacheInterceptor.response(response);
  },
  async (error) => {
    // Safe error handling
    if (error && error.response) {
      console.error('❌ Response Error:', error.response.status, error.response.data.message);
      
      // Handle specific error cases
      // 401: לא מבצעים כאן redirect כדי לאפשר למנגנון refresh לתפוס
      
      // Clear cache for failed requests
      if (error.config && error.config.method === 'get') {
        const cacheKey = `${error.config.method}:${error.config.url}:${JSON.stringify(error.config.params)}`;
        apiCache.delete(cacheKey);
      }
    } else {
      console.error('❌ Network Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Utility functions
export const clearApiCache = () => {
  apiCache.clear();
  console.log('🧹 API cache cleared');
};

export const getCacheStats = () => {
  return {
    size: apiCache.size,
    keys: Array.from(apiCache.keys())
  };
};

// Enhanced API functions with retry
export const apiGet = async (url, config = {}) => {
  return retryRequest({ ...config, method: 'get', url });
};

export const apiPost = async (url, data = {}, config = {}) => {
  return retryRequest({ ...config, method: 'post', url, data });
};

export const apiPut = async (url, data = {}, config = {}) => {
  return retryRequest({ ...config, method: 'put', url, data });
};

export const apiDelete = async (url, config = {}) => {
  return retryRequest({ ...config, method: 'delete', url });
};

export const apiPatch = async (url, data = {}, config = {}) => {
  return retryRequest({ ...config, method: 'patch', url, data });
};

// פונקציה לקבלת כתובת API
export const getApiUrl = (endpoint) => {
  return import.meta.env.DEV ? `/api${endpoint}` : `/api${endpoint}`;
};

// פונקציה לקבלת כתובת שרת
export const getServerUrl = () => {
  return import.meta.env.DEV ? '' : '';
};

export default axiosInstance;
